import React, { useState, useEffect } from 'react';
import PublisherApiService from '../../ApiServices/PublisherApiService/PublisherApiService';
import { ToastContainer, toast } from 'react-toastify';
import { FaCheck, FaTimes } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import { User, Mail, Building, Phone, FileText, MoreVertical } from "lucide-react";
import DataTable from 'react-data-table-component';
import { DataStyles } from '../Common/DataTableStyles';


const PublishersTable = () => {
    const [allPublishers, setAllPublishers] = useState([]); // Store all publishers
    const [publishers, setPublishers] = useState([]); // Store filtered publishers
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPublisher, setSelectedPublisher] = useState(null); // For form view

    function loadInit() {
        const fetchPublishers = async () => {
            setLoading(true);
            try {
                const response = await PublisherApiService.get(); // Fetch publishers from API
                setAllPublishers(response.data); // Store all publishers
                setPublishers(response.data); // Display all publishers initially
            } catch (error) {
                console.error(error);
                toast.error('Failed to fetch publishers.');
            } finally {
                setLoading(false);
            }
        };

        fetchPublishers();
    }

    useEffect(loadInit, []);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (value.trim() === '') {
            // If search box is empty, show all publishers
            setPublishers(allPublishers);
        } else {
            const filtered = allPublishers.filter(publisher =>
                publisher.name.toLowerCase().includes(value.toLowerCase()) ||
                publisher.email.toLowerCase().includes(value.toLowerCase()) ||
                publisher.company.toLowerCase().includes(value.toLowerCase()) ||
                publisher.phone.includes(value)
            );
            setPublishers(filtered);
        }
    };

    const toggleInvoiceStatus = async (id, currentStatus) => {
        try {
            await PublisherApiService.updateInvoice(id); // API call to toggle invoice
            const updatedPublishers = publishers.map(publisher =>
                publisher.id === id ? { ...publisher, isInvoiceEnable: !currentStatus } : publisher
            );
            setPublishers(updatedPublishers);
            toast.success('Invoice status updated successfully.');
        } catch (error) {
            console.error(error);
            toast.error('Failed to update invoice status.');
        }
    };

    const approvePublisher = async (id) => {
        try {
            await PublisherApiService.ApprovePublisher(id); // API call to toggle invoice
            loadInit();
            toast.success('Publisher has been approved successfully.');
        } catch (error) {
            console.error(error);
            toast.error('Failed to update invoice status.');
        }
    };


    const showDetails = (publisher) => {
        setSelectedPublisher(publisher); // Set selected publisher for form view
    };

    const deletePublisher = async (id) => {
        if (window.confirm('Are you sure you want to delete this publisher?')) {
            try {
                await PublisherApiService.DeletePublisher(id); // Call the delete API
                setAllPublishers(allPublishers.filter(publisher => publisher.id !== id)); // Update original list
                setPublishers(publishers.filter(publisher => publisher.id !== id)); // Update visible list
                toast.success('Publisher deleted successfully.');
            } catch (error) {
                console.error(error);
                toast.error('Failed to delete publisher.');
            }
        }
    };
    const headers = [
        { label: "No", icon: "", selector: "no" },
        { label: "Name", icon: <User size={16} />, selector: "name" },
        { label: "Email", icon: <Mail size={16} />, selector: "email" },
        { label: "Company", icon: <Building size={16} />, selector: "company" },
        { label: "Phone", icon: <Phone size={16} />, selector: "phone" },
        { label: "Is Invoice Enabled", icon: <FileText size={16} />, selector: "isInvoiceEnable" },
        { label: "Actions", icon: <MoreVertical size={16} />, selector: "" }
    ];
    const columns = headers.map(({ label, icon, selector }) => {
        if (label === "Actions") {
            return {
                name: (
                    <div className="flex items-center">
                        {icon}
                        <span className="ml-2">{label}</span>
                    </div>
                ),
                cell: (publisher) => (
                    <div>
                        <button
                            onClick={() => toggleInvoiceStatus(publisher.id, publisher.isInvoiceEnable)}
                            className="bg-blue-500 mt-2 mb-1 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                        >
                            {publisher.isInvoiceEnable ? 'Disable' : 'Enable'}
                        </button>
                        {
                            (!publisher.isApprovedByAdmin && <button
                                onClick={() => approvePublisher(publisher.id)}
                                className="bg-green-500 my-1 text-white px-4 py-2 rounded-md hover:bg-green-600"
                            >
                                Approve
                            </button>
                            )
                        }
                        <button
                            onClick={() => showDetails(publisher)}
                            className="bg-yellow-500 my-1 text-white px-4 py-2 rounded-md hover:bg-yellow-600"
                        >
                            Details
                        </button>
                        <button
                            onClick={() => deletePublisher(publisher.id)}
                            className="bg-red-500 mb-2 mt-1 text-white px-4 py-2 rounded-md hover:bg-red-600"
                        >
                            Delete
                        </button>
                    </div>
                ),
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
            };
        } else {
            return {
                name: (
                    <div className="flex items-center">
                        {icon}
                        <span className="ml-2">{label}</span>
                    </div>
                ),
                selector: (row, index) => {
                    if (selector === "no") {
                        return (index + 1).toString();
                    } else if (selector === "isInvoiceEnable") {
                        return row[selector]
                            ? <FaCheck className="text-green-500" />
                            : <FaTimes className="text-red-500" />;
                    } else {
                        return row[selector]?.toString() ?? "";
                    }
                },
                sortable: true,
            };
        }
    });
    return (
        <div className="overflow-x-auto p-5">
            <ToastContainer />
            {/* Search Box */}
            <div className="flex justify-end mb-4">
                <input
                    type="text"
                    className="border border-gray-300 px-4 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Search by Name, Email, Company, or Phone..."
                    value={searchTerm}
                    onChange={handleSearch}
                />
            </div>

            {loading ? (
                <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-500"></div>
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={publishers}
                    pagination
                    highlightOnHover
                    striped
                    responsive
                    className="min-w-full bg-white border-collapse"
                    customStyles={DataStyles}
                />
            )}

            {/* Form View */}
            {selectedPublisher && (
                <div className="mt-5 p-5 border rounded-lg shadow-md bg-gray-100">
                    <h2 className="text-lg font-bold mb-3">Publisher Details</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="font-semibold">Name:</label>
                            <p>{selectedPublisher.name}</p>
                        </div>
                        <div>
                            <label className="font-semibold">Email:</label>
                            <p>{selectedPublisher.email}</p>
                        </div>
                        <div>
                            <label className="font-semibold">Company:</label>
                            <p>{selectedPublisher.company}</p>
                        </div>
                        <div>
                            <label className="font-semibold">Phone:</label>
                            <p>{selectedPublisher.phone}</p>
                        </div>
                        <div>
                            <label className="font-semibold">Is Invoice Enabled:</label>
                            <p>{selectedPublisher.isInvoiceEnable ? 'Yes' : 'No'}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => toggleInvoiceStatus(selectedPublisher.id, selectedPublisher.isInvoiceEnable)}
                        className="mt-4 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                    >
                        {selectedPublisher.isInvoiceEnable ? 'Disable Invoice' : 'Enable Invoice'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default PublishersTable;
